"use client";

import Image from "next/image";
import Link from "next/link";
import { useState, type ReactNode } from "react";
import { ArrowRightIcon, BellIcon, ChevronRightIcon, MusicIcon, SnowIcon, SunIcon } from "./icons";
import type { HomeBoardRole, HomeRolesView, HomeSeasonView, SeasonGroup } from "./home-data";

/**
 * Client island for the hero roles box. The server builds every season's
 * rows and counts; this only tracks the chosen season pin.
 */

const SEASON_ICON: Record<SeasonGroup, () => ReactNode> = {
  snow: SnowIcon,
  sun: SunIcon,
  festival: MusicIcon,
};

const STATE_TAG: Record<HomeBoardRole["state"], { label: string; className: string }> = {
  open: { label: "Open", className: "bg-[#E3F3EC] text-[#0B6B4F]" },
  filled: { label: "Filled", className: "bg-[#EEF0EB] text-[#566072]" },
  closed: { label: "Closed", className: "bg-[#EEF0EB] text-[#566072]" },
};

const ROW_CLASS =
  "flex items-center gap-3 border-b border-[#EEF0EB] bg-white py-2.5 pl-4 pr-3.5 no-underline transition-colors hover:bg-[#F4F8F6] md:gap-3.5 md:px-6 md:py-3";

function RoleLogo({ role }: { role: HomeBoardRole }) {
  const muted = role.state !== "open" ? "opacity-75" : "";
  if (role.logo) {
    const onNavy = role.logo.tone === "light";
    return (
      <span
        aria-hidden="true"
        className={`relative flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-[10px] border p-1 ${
          onNavy ? "border-navy bg-navy" : "border-line bg-white"
        } ${muted}`}
      >
        <span className="relative block h-full w-full">
          <Image src={role.logo.src} alt="" fill sizes="40px" className="object-contain" />
        </span>
      </span>
    );
  }
  return (
    <span
      aria-hidden="true"
      className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] border border-[#D3E6EE] bg-[#E6F2F7] text-[12.5px] font-bold tracking-[0.02em] text-[#2B6C85] ${muted}`}
    >
      {role.companyInitials}
    </span>
  );
}

function RoleRow({ role }: { role: HomeBoardRole }) {
  const open = role.state === "open";
  const tag = STATE_TAG[role.state];
  const content = (
    <>
      <RoleLogo role={role} />
      <span className="flex min-w-0 grow flex-col gap-px">
        <span className={`text-[15px] font-bold leading-[1.3] ${open ? "text-navy" : "text-[#566072]"}`}>{role.title}</span>
        <span className="truncate text-[13px] leading-[1.35] text-[#566072]">{role.companyName}</span>
        <span className="mt-px flex min-w-0 items-center gap-1.5 text-[12px] leading-[1.35] text-[#566072]">
          <span
            className={`inline-flex h-[18px] shrink-0 items-center rounded-full px-[7px] text-[11px] font-bold ${tag.className}`}
          >
            {tag.label}
          </span>
          <span className="min-w-0 truncate">
            {role.locationLabel} · {role.when}
          </span>
        </span>
        {open ? null : (
          <span className="sr-only">{role.href ? `. ${tag.label}, view employer` : `. ${tag.label}`}</span>
        )}
      </span>
      <span className="text-[#9AA0A8]">
        <ChevronRightIcon />
      </span>
    </>
  );
  if (!role.href) {
    return <div className={ROW_CLASS}>{content}</div>;
  }
  return (
    <Link
      href={role.href}
      data-ph-capture-attribute-home-cta={open ? "hero-role" : "hero-role-closed"}
      className={ROW_CLASS}
    >
      {content}
    </Link>
  );
}

function rolesPosted(count: number): string {
  return `${count.toLocaleString("en-GB")} ${count === 1 ? "role" : "roles"} posted`;
}

function seasonMessage(season: HomeSeasonView): string {
  const shown = season.rows.length;
  const lead =
    shown === 0
      ? `No ${season.word} roles posted yet.`
      : `Showing the ${shown === 1 ? "newest" : `${shown} newest`} ${season.word} ${shown === 1 ? "role" : "roles"}.`;
  const counts = `${lead} ${season.total.toLocaleString("en-GB")} posted, ${season.open.toLocaleString("en-GB")} open.`;
  return season.note ? `${counts} ${season.note}` : counts;
}

export function HomeRolesBox({ view }: { view: HomeRolesView }) {
  const [group, setGroup] = useState<SeasonGroup>(view.defaultGroup);
  const season = view.seasons.find((item) => item.group === group) ?? view.seasons[0];

  return (
    <section
      aria-labelledby="home-roles-h"
      className="relative flex flex-col overflow-hidden rounded-[22px] border border-line bg-white shadow-[0_24px_60px_rgba(22,35,59,0.2)] lg:border-0 lg:shadow-[0_30px_80px_rgba(8,14,26,0.4),0_2px_8px_rgba(8,14,26,0.12)]"
    >
      <span
        aria-hidden="true"
        className="block h-[3px] animate-pw-tick bg-[linear-gradient(90deg,#16808F_0%,rgba(255,255,255,0)_100%)]"
      />
      <p className="mx-4 mt-3 self-start rounded-full bg-[#EAEDE5] px-3 py-1 text-[12.5px] font-semibold leading-[1.4] text-[#566072] md:mx-6 md:mt-4">
        PeakWave launched 2026
      </p>
      <div className="flex items-center justify-between gap-3 px-4 pb-1 pt-2.5 md:px-6 md:pt-3">
        <h2
          id="home-roles-h"
          className="m-0 flex items-center gap-2 text-[19px] font-bold leading-[1.25] text-navy md:text-[22px]"
        >
          Roles posted{" "}
          <span
            aria-hidden="true"
            className="inline-flex h-[22px] min-w-6 items-center justify-center rounded-full bg-[#EEF6F7] px-[7px] text-[12.5px] font-bold text-teal-dark md:h-[26px] md:text-[14px]"
          >
            {season.total.toLocaleString("en-GB")}
          </span>
          <span className="sr-only">, {rolesPosted(season.total)}</span>
        </h2>
        <span className="whitespace-nowrap text-[12px] font-semibold text-[#566072]">Newest first</span>
      </div>

      <div
        role="group"
        aria-label="Choose a season"
        className="grid grid-cols-3 gap-1.5 border-b border-[#EEF0EB] px-3 pb-3 pt-2 md:px-5"
      >
        {view.seasons.map((item) => {
          const active = item.group === group;
          const Icon = SEASON_ICON[item.group];
          return (
            <button
              key={item.group}
              type="button"
              aria-pressed={active}
              aria-label={`${item.pinLabel}, ${rolesPosted(item.total)}`}
              onClick={() => setGroup(item.group)}
              data-ph-capture-attribute-home-cta={`hero-season-${item.group}`}
              className={`flex min-h-11 min-w-0 items-center justify-center gap-[5px] whitespace-nowrap rounded-full border px-2 text-[13.5px] font-semibold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-dark ${
                active ? "border-navy bg-navy text-white" : "border-line bg-[#F4F6F2] text-navy hover:bg-[#EEF2EC]"
              }`}
            >
              <Icon />
              <span>{item.pinLabel}</span>
              <span
                className={`inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-[5px] text-[11px] font-bold ${
                  active
                    ? "bg-white/20 text-white"
                    : item.total > 0
                      ? "bg-white text-[#566072]"
                      : "border border-[#D5DBD2] text-[#566072]"
                }`}
              >
                {item.total.toLocaleString("en-GB")}
              </span>
            </button>
          );
        })}
      </div>
      <p className="sr-only" aria-live="polite">
        {seasonMessage(season)}
      </p>

      {season.rows.length === 0 ? (
        <p className="m-0 border-b border-[#EEF0EB] px-4 py-4 text-[14px] leading-[1.5] text-[#566072] md:px-6">
          No {season.word} roles posted yet. Switch on alerts and we will tell you when new ones go up.
        </p>
      ) : (
        <ul className="m-0 flex list-none flex-col p-0">
          {season.rows.map((role) => (
            <li key={role.id}>
              <RoleRow role={role} />
            </li>
          ))}
        </ul>
      )}

      {season.note ? (
        <p className="mx-4 mb-0 mt-2.5 text-[13px] leading-[1.45] text-[#566072] md:mx-6">{season.note}</p>
      ) : null}

      <div className="flex flex-col items-stretch px-4 pb-1 pt-3 md:px-6 md:pt-4">
        <Link
          href={season.cta.href}
          data-ph-capture-attribute-home-cta={season.cta.kind === "jobs" ? "hero-see-open-roles" : "hero-roles-empty-alerts"}
          className="inline-flex min-h-[50px] items-center justify-center gap-2 whitespace-nowrap rounded-[13px] bg-teal px-[18px] text-[15px] font-semibold text-white no-underline shadow-[0_10px_22px_rgba(28,138,153,0.24)] transition-colors hover:bg-teal-dark"
        >
          {season.cta.label} <ArrowRightIcon />
        </Link>
        {season.cta.kind === "jobs" ? (
          <Link
            href={view.alertsHref}
            data-ph-capture-attribute-home-cta="hero-job-alerts"
            className="inline-flex min-h-11 items-center gap-1.5 self-center whitespace-nowrap px-3 text-[13px] font-semibold text-[#566072] no-underline hover:text-navy"
          >
            <BellIcon />
            Get alerts
          </Link>
        ) : (
          <span aria-hidden="true" className="h-3" />
        )}
      </div>
    </section>
  );
}
